import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { LEAD_STATUS_META, formatUSD } from "@/lib/format";
import LeadToolCards from "@/components/LeadToolCards";
import AdminTimeline from "@/components/AdminTimeline";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUSES = ["new", "contacted", "followed_up", "lost", "spam"];

const AdminLeadDetail = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [closeReason, setCloseReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = () => api.get(`/admin/leads/${id}`).then((r) => setLead(r.data)).catch(() => setLead(null));
  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const doOverride = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${id}`, {
        status: newStatus,
        override_reason: reason,
        close_reason: (newStatus === "sold" || newStatus === "lost") ? closeReason : undefined,
      });
      toast.success(`Lead status overridden to ${newStatus}`);
      setOverrideOpen(false);
      setReason("");
      setCloseReason("");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!lead) return <AdminPage title="Loading…" testId="admin-lead-detail-page"><div className="h-96 bg-slate-100 rounded-2xl animate-pulse" /></AdminPage>;
  const meta = LEAD_STATUS_META[lead.status] || LEAD_STATUS_META.new;
  const customerName = lead.customer?.full_name || lead.guest_name || "—";

  return (
    <AdminPage
      title={`Lead — ${customerName}`}
      subtitle={`Created ${new Date(lead.created_at).toLocaleString()}`}
      testId="admin-lead-detail-page"
      action={
        <div className="flex gap-2">
          <Button asChild variant="outline"><Link to="/admin/leads"><ArrowLeft size={13} /> All leads</Link></Button>
          <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => { setNewStatus(lead.status); setOverrideOpen(true); }} data-testid="admin-lead-override-open-btn">
            <ShieldAlert size={14} /> Override status
          </Button>
        </div>
      }
    >
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Contact</p>
          <p className="mt-2 font-semibold">{customerName}</p>
          <p className="text-sm text-slate-600">{lead.customer?.email || lead.guest_email}</p>
          <p className="text-sm text-slate-600">{lead.customer?.phone || lead.guest_phone}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Vehicle</p>
          {lead.vehicle ? (
            <>
              <p className="mt-2 font-semibold">{lead.vehicle.year} {lead.vehicle.make} {lead.vehicle.model}</p>
              <p className="text-sm text-slate-600">{formatUSD(lead.vehicle.price)} · {lead.vehicle.mileage?.toLocaleString()} mi</p>
              <Link to={`/vehicle/${lead.vehicle.id}`} className="text-xs text-brand hover:underline">View public listing →</Link>
            </>
          ) : <p className="mt-2 text-slate-400 text-sm">Vehicle removed</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Status & assignment</p>
          <p className="mt-2"><span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.tone}`} data-testid="admin-lead-status-pill">{meta.label}</span></p>
          <p className="mt-2 text-sm text-slate-600"><span className="text-slate-400">Dealer:</span> {lead.dealer?.name}</p>
          <p className="text-sm text-slate-600"><span className="text-slate-400">BDC:</span> {lead.bdc_agent?.full_name || <span className="italic text-slate-400">Unclaimed</span>}</p>
        </div>
      </div>

      {/* Phase 7 — financing / trade-in context cards for the admin */}
      <LeadToolCards lead={lead} className="grid md:grid-cols-2 gap-4 mt-4" />

      {/* Notes (dealer + bdc + system audit) */}
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Notes (BDC + admin overrides)</p>
          <ul className="mt-3 space-y-2">
            {(lead.bdc_notes || []).length === 0 && <li className="text-xs text-slate-400">No notes yet.</li>}
            {(lead.bdc_notes || []).map((n) => (
              <li key={n.id} className="text-sm border-l-2 border-brand/30 pl-3">
                <p className="text-slate-800 whitespace-pre-wrap">{n.body}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{n.author_name} · {new Date(n.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Dealer notes</p>
          <ul className="mt-3 space-y-2">
            {(lead.dealer_notes || []).length === 0 && <li className="text-xs text-slate-400">No dealer notes.</li>}
            {(lead.dealer_notes || []).map((n) => (
              <li key={n.id} className="text-sm border-l-2 border-slate-300 pl-3">
                <p className="text-slate-800 whitespace-pre-wrap">{n.body}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{n.author_name} · {new Date(n.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Phase 11 — Unified timeline of leads/chats/notes/calls/emails/appts */}
      <div className="mt-6">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Timeline</p>
        <AdminTimeline endpoint={`/admin/leads/${id}/timeline`} />
      </div>

      {/* Audit trail specific to this lead */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Admin audit trail</p>
        {(lead.audit_entries || []).length === 0 ? (
          <p className="mt-3 text-xs text-slate-400">No admin actions on this lead.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {lead.audit_entries.map((a) => (
              <li key={a.id} className="text-sm py-2 border-b border-slate-100 last:border-0">
                <span className="font-semibold text-slate-800">{a.action}</span>
                <span className="ml-2 text-xs text-slate-500">{a.admin_user_email} · {new Date(a.created_at).toLocaleString()}</span>
                <pre className="mt-1 text-[11px] text-slate-600 bg-slate-50 p-2 rounded max-w-full overflow-auto">{JSON.stringify(a.details, null, 2)}</pre>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Override dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent data-testid="admin-lead-override-dialog">
          <DialogHeader>
            <DialogTitle>Override lead status</DialogTitle>
            <DialogDescription>
              Bypasses the normal state machine and records this action in the audit log. Use for corrections only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">New status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="admin-lead-override-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{LEAD_STATUS_META[s]?.label || s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Override reason (required)</Label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this manual override necessary?" data-testid="admin-lead-override-reason-input" />
            </div>
            {(newStatus === "sold" || newStatus === "lost") && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Close reason (required)</Label>
                <Textarea rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} data-testid="admin-lead-close-reason" />
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={doOverride} disabled={saving || !reason.trim() || !newStatus || newStatus === lead.status} className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-lead-override-submit-btn">
              {saving ? "Saving…" : "Override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default AdminLeadDetail;
